import React, { useState, useEffect } from 'react';
import { Lightbulb, Sparkles, TrendingUp, Target, Zap, RefreshCw, CheckCircle, AlertCircle, Clock, BarChart3, Settings, FileText, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AIService from '../../services/aiService';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import { useToast, ToastContainer } from '../common/Toast';
import ConfirmationModal from '../common/ConfirmationModal';

const SmartSuggestions = () => {
  const navigate = useNavigate();
  const { success, error: showError, info } = useToast();
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [showImplementModal, setShowImplementModal] = useState(false);
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);

  const categories = [
    { id: 'all', name: 'All Suggestions', icon: <Sparkles size={16} /> },
    { id: 'contracts', name: 'Contracts', icon: <FileText size={16} /> },
    { id: 'assets', name: 'Assets', icon: <TrendingUp size={16} /> },
    { id: 'processes', name: 'Processes', icon: <Zap size={16} /> }
  ];

  const generateSuggestions = async () => {
    setIsLoading(true);
    try {
      const response = await AIService.generateSuggestions(['contracts', 'assets', 'processes']);
      
      if (response.success) {
        setSuggestions(response.suggestions || []);
        setLastUpdated(new Date());
        success(`Generated ${response.suggestions?.length || 0} intelligent suggestions`);
      } else {
        showError('Failed to generate suggestions');
      }
    } catch (error) {
      console.error('Error generating suggestions:', error);
      showError(error.message || 'Failed to generate suggestions');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredSuggestions = selectedCategory === 'all' 
    ? suggestions 
    : suggestions.filter(s => s.category === selectedCategory);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High': return '#ef4444';
      case 'Medium': return '#f59e0b';
      case 'Low': return '#10b981';
      default: return '#6b7280';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'High': return <AlertCircle size={16} />;
      case 'Medium': return <Clock size={16} />;
      case 'Low': return <CheckCircle size={16} />;
      default: return <Clock size={16} />;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'contracts': return 'var(--primary-color)';
      case 'assets': return '#10b981';
      case 'processes': return '#8b5cf6';
      default: return 'var(--primary-color)';
    }
  };

  const getCategoryGradient = (category) => {
    switch (category) {
      case 'contracts': return 'linear-gradient(135deg, var(--primary-color) 0%, #1d4ed8 100%)';
      case 'assets': return 'linear-gradient(135deg, #10b981 0%, #059669 100%)';
      case 'processes': return 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)';
      default: return 'linear-gradient(135deg, var(--primary-color) 0%, #1d4ed8 100%)';
    }
  };

  const handleImplementSuggestion = () => {
    if (!selectedSuggestion) return;
    
    // Mark suggestion as implemented
    setSuggestions(prev => prev.map(s => 
      s.id === selectedSuggestion.id 
        ? { ...s, status: 'implemented', implementedAt: new Date().toISOString() }
        : s
    ));
    
    // Navigate based on category
    if (selectedSuggestion.category === 'contracts') {
      navigate('/contracts');
      success('Navigating to Contracts page. You can now implement the suggestion there.');
    } else if (selectedSuggestion.category === 'assets') {
      navigate('/assets');
      success('Navigating to Assets page. You can now implement the suggestion there.');
    } else {
      // For processes, show a success message
      success(`Suggestion "${selectedSuggestion.title}" has been marked as implemented.`);
    }
    
    setShowImplementModal(false);
  };

  const handleViewAnalytics = () => {
    if (!selectedSuggestion) return;
    // Navigate to analytics page or show analytics modal
    info('Analytics feature coming soon. This will show ROI analysis and implementation metrics.');
  };

  useEffect(() => {
    // Load initial suggestions on component mount
    generateSuggestions();
  }, []);

  return (
    <>
      <PageBreadcrumb />
      <PageHeader 
        title="Smart Suggestions"
        description="Receive intelligent recommendations for contract improvements and asset optimization."
        actions={[
          {
            label: isLoading ? 'Generating...' : 'Generate New',
            icon: <RefreshCw size={18} style={{ animation: isLoading ? 'spin 1s linear infinite' : 'none' }} />,
            onClick: generateSuggestions,
            variant: 'primary',
            disabled: isLoading
          }
        ]}
      />
      
      <div className="page-container">
        {/* Main Content - 2 Column Layout */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: '24px', marginTop: '24px' }}>
          {/* Left Column - Suggestions List */}
          <div>
            {/* Filter Section */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '0',
              padding: '20px',
              marginBottom: '20px',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px'
              }}>
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#1f2937',
                  margin: 0
                }}>
                  AI-Powered Suggestions
                </h3>
                <span style={{
                  fontSize: '13px',
                  color: '#6b7280'
                }}>
                  {suggestions.length} suggestion{suggestions.length !== 1 ? 's' : ''} generated
                  {lastUpdated && (
                    <span style={{ marginLeft: '8px', color: '#10b981' }}>
                      <Clock size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px' }} />
                      {lastUpdated.toLocaleTimeString()}
                    </span>
                  )}
                </span>
              </div>
              
              <div style={{
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '12px'
              }}>
                Filter by Category
              </div>
              
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    disabled={isLoading}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '8px 14px',
                      backgroundColor: selectedCategory === category.id ? 'var(--primary-color)' : 'transparent',
                      color: selectedCategory === category.id ? 'white' : 'var(--primary-color)',
                      border: `1px solid ${selectedCategory === category.id ? 'var(--primary-color)' : '#d1d5db'}`,
                      borderRadius: '0',
                      fontSize: '13px',
                      fontWeight: '500',
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                      transition: 'all 0.2s',
                      opacity: isLoading ? 0.6 : 1
                    }}
                  >
                    {category.icon}
                    {category.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Suggestions List */}
            <div style={{
              maxHeight: 'calc(100vh - 400px)',
              overflowY: 'auto',
              paddingRight: '8px'
            }}>
              {isLoading ? (
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '0',
                  padding: '60px 20px',
                  textAlign: 'center',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    border: '4px solid #f3f4f6',
                    borderTop: '4px solid var(--primary-color)',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto 20px'
                  }} />
                  <h4 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#6b7280',
                    margin: '0 0 8px 0'
                  }}>
                    Generating intelligent suggestions...
                  </h4>
                  <p style={{
                    fontSize: '14px',
                    color: '#9ca3af',
                    margin: 0
                  }}>
                    This may take a few moments
                  </p>
                </div>
              ) : filteredSuggestions.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {filteredSuggestions.map((suggestion) => (
                    <div 
                      key={suggestion.id}
                      onClick={() => setSelectedSuggestion(suggestion)}
                      style={{
                        backgroundColor: selectedSuggestion?.id === suggestion.id ? '#f0f9ff' : 'white',
                        border: `2px solid ${selectedSuggestion?.id === suggestion.id ? 'var(--primary-color)' : '#f1f5f9'}`,
                        borderRadius: '0',
                        padding: '20px',
                        cursor: 'pointer',
                        transition: 'all 0.3s',
                        boxShadow: selectedSuggestion?.id === suggestion.id ? '0 4px 12px rgba(59, 130, 246, 0.15)' : '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                      }}
                      onMouseEnter={(e) => {
                        if (selectedSuggestion?.id !== suggestion.id) {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.1)';
                          e.currentTarget.style.borderColor = '#e2e8f0';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedSuggestion?.id !== suggestion.id) {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1)';
                          e.currentTarget.style.borderColor = '#f1f5f9';
                        }
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                        <div style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: getCategoryGradient(suggestion.category),
                          color: 'white',
                          flexShrink: 0
                        }}>
                          {suggestion.category === 'contracts' ? <FileText size={24} /> :
                           suggestion.category === 'assets' ? <TrendingUp size={24} /> :
                           <Zap size={24} />}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            marginBottom: '8px'
                          }}>
                            <h4 style={{
                              fontSize: '15px',
                              fontWeight: '600',
                              color: '#1f2937',
                              margin: 0
                            }}>
                              {suggestion.title}
                            </h4>
                            <span style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '4px 10px',
                              backgroundColor: getPriorityColor(suggestion.priority),
                              color: 'white',
                              borderRadius: '0',
                              fontSize: '11px',
                              fontWeight: '500'
                            }}>
                              {getPriorityIcon(suggestion.priority)}
                              {suggestion.priority}
                            </span>
                          </div>
                          <p style={{
                            fontSize: '13px',
                            color: '#6b7280',
                            margin: '0 0 8px 0',
                            lineHeight: '1.5'
                          }}>
                            {suggestion.description}
                          </p>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            fontSize: '12px',
                            color: '#9ca3af'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <span style={{ color: '#10b981', fontWeight: '600' }}>
                                {suggestion.estimatedSavings}
                              </span>
                              <span>{suggestion.impact}</span>
                            </div>
                            <span style={{
                              padding: '2px 8px',
                              backgroundColor: '#f3f4f6',
                              color: '#6b7280',
                              borderRadius: '0',
                              fontSize: '11px',
                              textTransform: 'capitalize'
                            }}>
                              {suggestion.category}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '0',
                  padding: '60px 20px',
                  textAlign: 'center',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 20px',
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    color: 'white'
                  }}>
                    <Lightbulb size={32} />
                  </div>
                  <h4 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#1f2937',
                    margin: '0 0 8px 0'
                  }}>
                    No suggestions available
                  </h4>
                  <p style={{
                    fontSize: '14px',
                    color: '#6b7280',
                    margin: '0 0 20px 0'
                  }}>
                    Click "Generate New" to get AI-powered recommendations.
                  </p>
                  <button 
                    onClick={generateSuggestions}
                    disabled={isLoading}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 20px',
                      backgroundColor: '#f59e0b',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                    }}
                  >
                    <Lightbulb size={16} />
                    Generate Suggestions
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Suggestion Details */}
          <div>
            {selectedSuggestion ? (
              <div style={{
                backgroundColor: 'white',
                borderRadius: '0',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                border: '1px solid #e5e7eb',
                height: 'fit-content',
                minHeight: '400px'
              }}>
                <div style={{
                  padding: '24px',
                  borderBottom: '1px solid #e5e7eb',
                  background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                      <div style={{
                        width: '56px',
                        height: '56px',
                        borderRadius: '0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: getCategoryGradient(selectedSuggestion.category),
                        color: 'white',
                        flexShrink: 0
                      }}>
                        {selectedSuggestion.category === 'contracts' ? <FileText size={28} /> :
                         selectedSuggestion.category === 'assets' ? <TrendingUp size={28} /> :
                         <Zap size={28} />}
                      </div>
                      <div>
                        <h3 style={{
                          fontSize: '20px',
                          fontWeight: '600',
                          color: '#1f2937',
                          margin: '0 0 12px 0'
                        }}>
                          {selectedSuggestion.title}
                        </h3>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <span style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '6px 12px',
                            backgroundColor: getPriorityColor(selectedSuggestion.priority),
                            color: 'white',
                            borderRadius: '0',
                            fontSize: '12px',
                            fontWeight: '500'
                          }}>
                            {getPriorityIcon(selectedSuggestion.priority)}
                            {selectedSuggestion.priority}
                          </span>
                          <span style={{
                            padding: '6px 12px',
                            backgroundColor: '#f3f4f6',
                            color: '#6b7280',
                            borderRadius: '0',
                            fontSize: '12px',
                            textTransform: 'capitalize'
                          }}>
                            {selectedSuggestion.category}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div style={{ padding: '24px' }}>
                  {/* Description */}
                  <div style={{ marginBottom: '24px' }}>
                    <h4 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#374151',
                      margin: '0 0 12px 0'
                    }}>
                      Description
                    </h4>
                    <div style={{
                      backgroundColor: '#f8fafc',
                      padding: '16px',
                      borderRadius: '0',
                      border: '1px solid #e5e7eb'
                    }}>
                      <p style={{
                        fontSize: '14px',
                        color: '#374151',
                        margin: 0,
                        lineHeight: '1.6'
                      }}>
                        {selectedSuggestion.description}
                      </p>
                    </div>
                  </div>

                  {/* Financial Impact */}
                  <div style={{ marginBottom: '24px' }}>
                    <h4 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#374151',
                      margin: '0 0 12px 0'
                    }}>
                      Financial Impact
                    </h4>
                    <div style={{
                      backgroundColor: 'rgba(16, 185, 129, 0.1)',
                      border: '1px solid rgba(16, 185, 129, 0.2)',
                      padding: '16px',
                      borderRadius: '0'
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}>
                        <div>
                          <h3 style={{
                            fontSize: '24px',
                            fontWeight: '700',
                            color: '#10b981',
                            margin: '0 0 4px 0'
                          }}>
                            {selectedSuggestion.estimatedSavings}
                          </h3>
                          <p style={{
                            fontSize: '13px',
                            color: '#6b7280',
                            margin: 0
                          }}>
                            {selectedSuggestion.impact}
                          </p>
                        </div>
                        <TrendingUp size={32} style={{ color: '#10b981' }} />
                      </div>
                    </div>
                  </div>

                  {/* Actionable Steps */}
                  <div style={{ marginBottom: '24px' }}>
                    <h4 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#374151',
                      margin: '0 0 16px 0',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <Sparkles size={16} style={{ color: 'var(--primary-color)' }} />
                      Actionable Steps
                    </h4>
                    <div>
                      {selectedSuggestion.actionableSteps.map((step, index) => (
                        <div key={index} style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '12px',
                          padding: '12px 0',
                          borderBottom: index < selectedSuggestion.actionableSteps.length - 1 ? '1px solid #e5e7eb' : 'none'
                        }}>
                          <div style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: getCategoryColor(selectedSuggestion.category),
                            marginTop: '6px',
                            flexShrink: 0
                          }} />
                          <span style={{
                            fontSize: '14px',
                            color: '#374151',
                            lineHeight: '1.6'
                          }}>
                            {step}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button 
                      onClick={() => setShowImplementModal(true)}
                      style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        padding: '12px 24px',
                        backgroundColor: 'var(--primary-color)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#1d4ed8';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--primary-color)';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      <Target size={18} />
                      Implement Suggestion
                    </button>
                    <button 
                      onClick={handleViewAnalytics}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        padding: '12px 24px',
                        backgroundColor: 'transparent',
                        color: 'var(--primary-color)',
                        border: '1px solid var(--primary-color)',
                        borderRadius: '0',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--primary-color)';
                        e.currentTarget.style.color = 'white';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = 'var(--primary-color)';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      <BarChart3 size={18} />
                      View Analytics
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div style={{
                backgroundColor: 'white',
                borderRadius: '0',
                padding: '60px 40px',
                textAlign: 'center',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                border: '1px solid #e5e7eb',
                height: 'fit-content',
                minHeight: '400px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <div style={{
                  width: '120px',
                  height: '120px',
                  borderRadius: '0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px',
                  background: 'linear-gradient(135deg, var(--primary-color) 0%, #8b5cf6 100%)',
                  color: 'white'
                }}>
                  <Lightbulb size={48} />
                </div>
                <h4 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#1f2937',
                  margin: '0 0 12px 0'
                }}>
                  Select a Suggestion
                </h4>
                <p style={{
                  fontSize: '14px',
                  color: '#6b7280',
                  margin: '0 0 24px 0',
                  maxWidth: '400px'
                }}>
                  Choose a suggestion from the list to view detailed information, 
                  implementation steps, and financial impact analysis.
                </p>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 20px',
                    backgroundColor: 'var(--primary-color)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                  }}>
                    <BarChart3 size={18} />
                    View Analytics
                  </button>
                  <button style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 20px',
                    backgroundColor: 'transparent',
                    color: 'var(--primary-color)',
                    border: '1px solid var(--primary-color)',
                    borderRadius: '0',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                  }}>
                    <Settings size={18} />
                    Settings
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <ConfirmationModal
        show={showImplementModal}
        onClose={() => setShowImplementModal(false)}
        onConfirm={handleImplementSuggestion}
        title="Implement Suggestion"
        message={`Are you ready to implement "${selectedSuggestion?.title}"? This will navigate you to the relevant page where you can start the implementation process.`}
        confirmText="Yes, Implement"
        cancelText="Cancel"
        variant="primary"
      />

      <ToastContainer />
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default SmartSuggestions;
